<script setup lang="ts">
  import { computed } from 'vue';
  import { IconButton, InfoTooltip, Tooltip } from '@/shared/ui';
  import { formatInstantDateToPtBR, formatInstantToPtBR } from '@/shared/lib';
  import { usePermission } from '@/entities/Permission';
  import {
    TRAINING_CONTENT_EXTENSION_LABELS,
    TRAINING_CONTENT_TYPE_LABELS,
    type TrainingContentListItem,
  } from '@/entities/Training';
  import { contentTableCellModifierClass } from '../model/useTrainingContentsTable';
  import EyeActionIcon from '@/shared/assets/icons/EyeActionIcon.svg';
  import PencilIcon from '@/shared/assets/icons/PencilBoldIcon.svg';
  import TrashIcon from '@/shared/assets/icons/TrashBoldIcon.svg';
  import InfoIcon from '@/shared/assets/icons/InfoBoldIcon.svg';

  const props = defineProps<{
    content: TrainingContentListItem;
  }>();

  const emit = defineEmits<{
    view: [content: TrainingContentListItem];
    edit: [content: TrainingContentListItem];
    delete: [content: TrainingContentListItem];
  }>();

  const { can } = usePermission();
  const canManageContents = can('readContent', 'Training');

  const contentAudit = computed(() => ({
    createdAt: formatInstantToPtBR(props.content.createdAt),
    createdBy: props.content.createdBy?.name,
    updatedAt: formatInstantToPtBR(props.content.updatedAt),
    updatedBy: props.content.updatedBy?.name,
  }));
</script>

<template>
  <tr class="border-b border-gray-02 hover:bg-gray-01 transition-colors">
    <td
      class="pl-6 py-4.5 text-sm text-gray-07 min-w-0"
      :class="contentTableCellModifierClass(0)"
    >
      <Tooltip :content="content.title" placement="top">
        <span class="block truncate">{{ content.title }}</span>
      </Tooltip>
    </td>
    <td class="pl-6 py-4.5 text-sm text-gray-07" :class="contentTableCellModifierClass(1)">
      {{ formatInstantDateToPtBR(content.createdAt) }}
    </td>
    <td class="pl-6 py-4.5 text-sm text-gray-07" :class="contentTableCellModifierClass(2)">
      {{ TRAINING_CONTENT_EXTENSION_LABELS[content.extension] }}
    </td>
    <td class="pl-6 py-4.5 text-sm text-gray-07" :class="contentTableCellModifierClass(3)">
      {{ TRAINING_CONTENT_TYPE_LABELS[content.contentType] }}
    </td>
    <td class="px-6 py-4.5" :class="contentTableCellModifierClass(4)">
      <div class="flex gap-2 items-center text-gray-xx-02">
        <Tooltip content="Visualizar Conteúdo" class="flex items-center">
          <IconButton
            :src="EyeActionIcon"
            alt="Visualizar Conteúdo"
            class="w-6 h-6"
            :on-click="() => emit('view', props.content)"
          />
        </Tooltip>
        <template v-if="canManageContents">
          <Tooltip content="Editar" class="flex items-center">
            <IconButton
              :src="PencilIcon"
              alt="Editar"
              class="w-6 h-6"
              :on-click="() => emit('edit', props.content)"
            />
          </Tooltip>
          <Tooltip content="Deletar" class="flex items-center">
            <IconButton
              :src="TrashIcon"
              alt="Deletar"
              class="w-6 h-6"
              :on-click="() => emit('delete', props.content)"
            />
          </Tooltip>
        </template>
        <InfoTooltip
          :icon="InfoIcon"
          :created-at="contentAudit.createdAt"
          :created-by="contentAudit.createdBy"
          :updated-at="contentAudit.updatedAt"
          :updated-by="contentAudit.updatedBy"
        >
          <IconButton :src="InfoIcon" alt="Informações" class="w-6 h-6" />
        </InfoTooltip>
      </div>
    </td>
  </tr>
</template>
